import { loadLocalImage } from './image-loader.ts';
// Client-side image compression — #135 PR A.
//
// Canvas-based re-encode to WebP at quality 0.80. Resizes to a max width
// (default 1920px) preserving aspect ratio. Runs in the browser only —
// no SharedArrayBuffer, no WASM, no heavy dependency. Image compression
// alone delivers ~70-85% size reduction on typical UI screenshots, which
// is what makes the 500MB cap math work for the screenshot path.
//
// Video compression (ffmpeg-wasm) ships in PR B and lives in a separate
// async-loaded module so the main widget bundle stays under 80 KB gzipped.
//
// Failure model: any unexpected error (canvas missing, image decode fail,
// toBlob returns null) returns the ORIGINAL blob with the original
// content-type. The reservation pipeline then proceeds with raw bytes.
// We never block the user from submitting a bug report because compression
// faltered — the layered cap defense already protects the workspace.

export type ImageContentType = 'image/png' | 'image/jpeg' | 'image/webp';

export interface CompressImageOptions {
  /** Quality for the canvas re-encode. Default 0.80; bump to 0.92 for high-fidelity workspaces. */
  quality?: number;
  /** Max output width in pixels. Larger images scale down preserving aspect. Default 1920. */
  maxWidth?: number;
  /** Skip compression entirely for inputs below this byte threshold. Default 50 KB. */
  skipBelowBytes?: number;
  /**
   * Pluggable for tests. Defaults to global `Image`. Returns a Promise that resolves
   * to an Image-shaped object once the blob is decoded.
   */
  loadImage?: (blob: Blob) => Promise<{ naturalWidth: number; naturalHeight: number }>;
  /**
   * Pluggable for tests. Defaults to `document.createElement('canvas')`. Tests can
   * inject a mock that captures `drawImage` calls and synthesizes the toBlob result.
   */
  createCanvas?: () => HTMLCanvasElement | OffscreenCanvas | null;
}

export interface CompressImageResult {
  /** Compressed (or pass-through) blob. */
  blob: Blob;
  /** Final content type — either 'image/webp' on success or the input's type on fallback. */
  contentType: ImageContentType;
  /** Filename with extension swapped to match the new content-type, or unchanged on fallback. */
  filename: string;
  /**
   * 'compressed' = output is a fresh canvas re-encode.
   * 'passthrough' = output is the original blob (skipped or fell back).
   */
  outcome: 'compressed' | 'passthrough';
  /** Reason for passthrough, when outcome === 'passthrough'. Useful for telemetry. */
  passthroughReason?:
    | 'below_threshold'
    | 'not_an_image'
    | 'image_load_failed'
    | 'canvas_unavailable'
    | 'webp_unsupported'
    | 'compression_yielded_larger';
}

export const DEFAULT_QUALITY = 0.8;
export const DEFAULT_MAX_WIDTH = 1920;
export const DEFAULT_SKIP_BELOW_BYTES = 50 * 1024;
const SUPPORTED_INPUT_TYPES: ReadonlyArray<string> = ['image/png', 'image/jpeg', 'image/webp'];

/**
 * Compress an image blob via canvas → WebP. Falls back to the original blob
 * on any unsupported / failure path so the upload pipeline never breaks.
 */
export async function compressImage(
  input: { blob: Blob; contentType: ImageContentType; filename: string },
  options: CompressImageOptions = {},
): Promise<CompressImageResult> {
  const quality = options.quality ?? DEFAULT_QUALITY;
  const maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH;
  const skipBelow = options.skipBelowBytes ?? DEFAULT_SKIP_BELOW_BYTES;

  if (!SUPPORTED_INPUT_TYPES.includes(input.contentType)) {
    return {
      blob: input.blob,
      contentType: input.contentType,
      filename: input.filename,
      outcome: 'passthrough',
      passthroughReason: 'not_an_image',
    };
  }

  if (input.blob.size <= skipBelow) {
    return {
      blob: input.blob,
      contentType: input.contentType,
      filename: input.filename,
      outcome: 'passthrough',
      passthroughReason: 'below_threshold',
    };
  }

  const loadImg = options.loadImage ?? defaultLoadImage;
  const createCanvas = options.createCanvas ?? defaultCreateCanvas;

  let img: { naturalWidth: number; naturalHeight: number };
  try {
    img = await loadImg(input.blob);
  } catch {
    return passthrough(input, 'image_load_failed');
  }

  const canvas = createCanvas();
  if (!canvas) {
    return passthrough(input, 'canvas_unavailable');
  }

  const scale = img.naturalWidth > maxWidth ? maxWidth / img.naturalWidth : 1;
  const outWidth = Math.max(1, Math.round(img.naturalWidth * scale));
  const outHeight = Math.max(1, Math.round(img.naturalHeight * scale));
  canvas.width = outWidth;
  canvas.height = outHeight;

  const ctx = canvas.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) {
    return passthrough(input, 'canvas_unavailable');
  }

  // The default loader returns an HTMLImageElement, which drawImage accepts;
  // tests injecting a stub need to return something compatible. We cast to
  // CanvasImageSource at the call site so TS doesn't reject the union.
  ctx.drawImage(img as unknown as CanvasImageSource, 0, 0, outWidth, outHeight);

  let compressed: Blob | null;
  try {
    compressed = await canvasToBlob(canvas, 'image/webp', quality);
  } catch {
    return passthrough(input, 'webp_unsupported');
  }
  if (!compressed) {
    return passthrough(input, 'webp_unsupported');
  }

  // canvas.toBlob may silently emit 'image/png' if the browser doesn't support
  // WebP encoding (older Safari). The returned blob's `.type` is the source
  // of truth — never assume the requested type.
  const actualType = (compressed.type || 'image/webp').toLowerCase();
  const finalContentType: ImageContentType =
    actualType === 'image/jpeg'
      ? 'image/jpeg'
      : actualType === 'image/png'
        ? 'image/png'
        : 'image/webp';

  if (compressed.size >= input.blob.size) {
    // Compression made it bigger (or equal) — keep the original to avoid
    // wasting cap bytes on an unproductive re-encode.
    return passthrough(input, 'compression_yielded_larger');
  }

  return {
    blob: compressed,
    contentType: finalContentType,
    filename: rewriteExtension(input.filename, finalContentType),
    outcome: 'compressed',
  };
}

function passthrough(
  input: { blob: Blob; contentType: ImageContentType; filename: string },
  reason: NonNullable<CompressImageResult['passthroughReason']>,
): CompressImageResult {
  return {
    blob: input.blob,
    contentType: input.contentType,
    filename: input.filename,
    outcome: 'passthrough',
    passthroughReason: reason,
  };
}

function rewriteExtension(filename: string, contentType: ImageContentType): string {
  const ext =
    contentType === 'image/webp' ? '.webp' : contentType === 'image/jpeg' ? '.jpg' : '.png';
  // Strip any existing extension at the tail (case-insensitive). If the
  // filename has no extension, just append.
  const stripped = filename.replace(/\.[^./\\]+$/i, '');
  return `${stripped}${ext}`;
}

function defaultCreateCanvas(): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  return document.createElement('canvas');
}

async function defaultLoadImage(blob: Blob): Promise<HTMLImageElement> {
  return loadLocalImage(blob);
}

function canvasToBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  type: string,
  quality: number,
): Promise<Blob | null> {
  if ('convertToBlob' in canvas && typeof canvas.convertToBlob === 'function') {
    // OffscreenCanvas
    return canvas.convertToBlob({ type, quality });
  }
  return new Promise((resolve) => {
    (canvas as HTMLCanvasElement).toBlob((b) => resolve(b), type, quality);
  });
}
